import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { Input, Label } from "@/components/ui/Input";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { HoverHintWithGlyph } from "@/components/ui/title-help";
import { ApiError } from "@/lib/api";

import {
  createAlternateOrder,
  OPEN_ALTERNATE_ORDERS_QUERY_KEY,
  type AlternateOrderSource,
  type CreateAlternateOrderBody,
} from "./alternateOrderApi";
import { CONFIGURATION_QUEUE_QUERY_KEY } from "./configurationApi";

const MODAL_HELP =
  "Alternate orders are planned builds that are not batch lines. They appear on the Configuration queue with a target " +
  "date and quantity. Configured units are marked as alternates. Leave allow encapsulation unchecked to hold units at " +
  "inventory on pre-encapsulation scan; check it to route through normal encapsulation.";

export interface CreateAlternateOrderModalPrefill {
  sku?: string;
  /** When more than one batch line SKU applies, show a picker instead of a blank field. */
  skuOptions?: string[];
  batchId?: number | null;
  salesOrderId?: number | null;
  targetDate?: string | null;
}

export function resolvePrefillSku(prefill: CreateAlternateOrderModalPrefill | undefined): string {
  const explicitSku = prefill?.sku?.trim() ?? "";
  if (explicitSku !== "") return explicitSku;
  const options = prefill?.skuOptions ?? [];
  return options.length === 1 ? options[0]!.trim() : "";
}

export function CreateAlternateOrderModal({
  open,
  onClose,
  source,
  prefill,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  source: AlternateOrderSource;
  prefill?: CreateAlternateOrderModalPrefill;
  onCreated?: (alternateOrderId: number) => void;
}): JSX.Element | null {
  const queryClient = useQueryClient();
  const [sku, setSku] = useState("");
  const [quantityText, setQuantityText] = useState("1");
  const [targetDate, setTargetDate] = useState("");
  const [allowEncapsulation, setAllowEncapsulation] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setSku("");
      setQuantityText("1");
      setTargetDate("");
      setAllowEncapsulation(false);
      setErrorMessage(null);
      return;
    }
    setSku(resolvePrefillSku(prefill));
    setTargetDate(prefill?.targetDate?.trim() ?? "");
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, prefill]);

  const createMutation = useMutation({
    mutationFn: (body: CreateAlternateOrderBody) => createAlternateOrder(body),
    onSuccess: (record) => {
      void queryClient.invalidateQueries({ queryKey: CONFIGURATION_QUEUE_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: OPEN_ALTERNATE_ORDERS_QUERY_KEY });
      onCreated?.(record.id);
      onClose();
    },
    onError: (error: unknown) => {
      setErrorMessage(error instanceof ApiError ? error.message : "Could not create alternate order.");
    },
  });

  if (!open) {
    return null;
  }

  const skuOptions = prefill?.skuOptions ?? [];
  const showSkuPicker = skuOptions.length > 1;

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    setErrorMessage(null);
    const trimmedSku = sku.trim();
    if (trimmedSku === "") {
      setErrorMessage("SKU is required.");
      return;
    }
    const quantity = Number.parseInt(quantityText.trim(), 10);
    if (!Number.isFinite(quantity) || quantity <= 0) {
      setErrorMessage("Quantity must be a positive integer.");
      return;
    }
    const trimmedDate = targetDate.trim();
    createMutation.mutate({
      sku: trimmedSku,
      quantity,
      targetDate: trimmedDate === "" ? null : trimmedDate,
      requiresEncapsulation: allowEncapsulation,
      source,
      batchId: prefill?.batchId ?? null,
      salesOrderId: prefill?.salesOrderId ?? null,
    });
  };

  return createPortal(
    <div
      {...hubModalOverlayProps(false)}
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-alternate-order-modal-title"
    >
      <form
        className="my-4 w-full max-w-md rounded-lg border border-slate-200 bg-white shadow-xl"
        onSubmit={handleSubmit}
      >
        <div className="border-b border-slate-200 px-4 py-3 sm:px-5">
          <div className="flex items-center gap-2">
            <h2 id="create-alternate-order-modal-title" className="text-lg font-semibold text-slate-900">
              Alternate order
            </h2>
            <HoverHintWithGlyph hoverHint={MODAL_HELP} size="md" />
          </div>
        </div>

        <div className="space-y-4 px-4 py-4 sm:px-5">
          <div className="space-y-2">
            <Label htmlFor="alternate-order-sku">SKU</Label>
            {showSkuPicker ? (
              <select
                id="alternate-order-sku"
                className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900"
                value={sku}
                autoFocus={sku.trim() === ""}
                onChange={(event) => setSku(event.target.value)}
              >
                <option value="">Select a SKU…</option>
                {skuOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            ) : (
              <Input
                id="alternate-order-sku"
                value={sku}
                placeholder="e.g. S04TRA-X"
                autoFocus={sku.trim() === ""}
                onChange={(event) => setSku(event.target.value)}
              />
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="alternate-order-quantity">Quantity</Label>
            <Input
              id="alternate-order-quantity"
              value={quantityText}
              inputMode="numeric"
              onChange={(event) => setQuantityText(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="alternate-order-target-date">Target date</Label>
            <Input
              id="alternate-order-target-date"
              type="date"
              value={targetDate}
              onChange={(event) => setTargetDate(event.target.value)}
            />
          </div>
          <label className="flex cursor-pointer items-start gap-2 text-sm text-slate-800">
            <input
              type="checkbox"
              className="mt-0.5 rounded border-slate-300"
              checked={allowEncapsulation}
              onChange={(event) => setAllowEncapsulation(event.target.checked)}
            />
            <span>Allow encapsulation</span>
          </label>
          {errorMessage ? <p className="text-sm text-rose-700">{errorMessage}</p> : null}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3 sm:px-5">
          <Button type="button" variant="secondary" onClick={onClose} disabled={createMutation.isPending}>
            Cancel
          </Button>
          <Button type="submit" isLoading={createMutation.isPending}>
            Create order
          </Button>
        </div>
      </form>
    </div>,
    document.body,
  );
}
